import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { device } from "../../../atoms/Utlis/media";
import {
  Plus,
  Trash2,
  Zap,
  GitBranch,
  Clock,
  Send,
  ArrowLeft,
  Sparkles,
  Eye,
  Save,
  Workflow,
} from "lucide-react";

type NodeType = "trigger" | "condition" | "delay" | "action";

interface WhatsAppWorkflowNode {
  id: string;
  type: NodeType;
  config: Record<string, unknown>;
}

const NODE_LABELS: Record<NodeType, string> = {
  trigger: "Trigger",
  condition: "Condition",
  delay: "Delay",
  action: "Action",
};

const NODE_ICONS: Record<NodeType, React.ReactNode> = {
  trigger: <Zap size={16} />,
  condition: <GitBranch size={16} />,
  delay: <Clock size={16} />,
  action: <Send size={16} />,
};

const NODE_TONES: Record<NodeType, string> = {
  trigger: "#c94daa",
  condition: "#7c4dff",
  delay: "#f09a3e",
  action: "#31a66a",
};

const AutomationBuilderPage: React.FC = () => {
  const [workflowName, setWorkflowName] = useState("");
  const [nodes, setNodes] = useState<WhatsAppWorkflowNode[]>([
    { id: "1", type: "trigger", config: { event: "new_message" } },
    { id: "2", type: "condition", config: {} },
    { id: "3", type: "action", config: { action: "send_template" } },
  ]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const addNode = (type: NodeType) => {
    setNodes((prev) => [...prev, { id: String(Date.now()), type, config: {} }]);
  };

  const removeNode = (id: string) => {
    setNodes((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <Page>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn to="/dashboard/whatsapp" aria-label="Go back">
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                WhatsApp workflows
              </HeroBadge>
              <PageTitle className="linear-text">Automation</PageTitle>
              <PageSubtitle>
                Build workflows: trigger → condition → action
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
          <HeroActions>
            <NameInput
              placeholder="Workflow name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
            />
            <SecondaryBtn type="button" onClick={() => setPreviewOpen((v) => !v)}>
              <Eye size={15} />
              {previewOpen ? "Hide preview" : "Preview flow"}
            </SecondaryBtn>
          </HeroActions>
        </HeroContent>
      </Hero>

      <StatsStrip>
        <StatChip>
          <StatIcon $tone="#c94daa">
            <Workflow size={16} />
          </StatIcon>
          <div>
            <StatValue>{nodes.length}</StatValue>
            <StatLabel>Steps</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#c94daa">
            <Zap size={16} />
          </StatIcon>
          <div>
            <StatValue>{nodes.filter((n) => n.type === "trigger").length}</StatValue>
            <StatLabel>Triggers</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#7c4dff">
            <GitBranch size={16} />
          </StatIcon>
          <div>
            <StatValue>{nodes.filter((n) => n.type === "condition").length}</StatValue>
            <StatLabel>Conditions</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#31a66a">
            <Send size={16} />
          </StatIcon>
          <div>
            <StatValue>{nodes.filter((n) => n.type === "action").length}</StatValue>
            <StatLabel>Actions</StatLabel>
          </div>
        </StatChip>
      </StatsStrip>

      <ContentRow $preview={previewOpen}>
        <FlowPanel>
          <PanelHeader>
            <PanelTitleRow>
              <Workflow size={16} color="#c94daa" />
              <PanelTitle>Flow builder</PanelTitle>
            </PanelTitleRow>
            <PanelHint>Connect triggers, conditions, delays, and actions</PanelHint>
          </PanelHeader>

          <FlowBody>
            <FlowSteps>
              {nodes.map((node, index) => (
                <React.Fragment key={node.id}>
                  <StepCard>
                    <StepBadge $tone={NODE_TONES[node.type]}>
                      {NODE_ICONS[node.type]}
                      {NODE_LABELS[node.type]}
                    </StepBadge>
                    <StepConfig>
                      {node.type === "trigger" && (
                        <ConfigSelect defaultValue="new_message">
                          <option value="new_message">New message received</option>
                          <option value="opt_in">Opt-in</option>
                        </ConfigSelect>
                      )}
                      {node.type === "condition" && (
                        <ConfigSelect defaultValue="">
                          <option value="">Add condition</option>
                          <option value="keyword">Contains keyword</option>
                          <option value="tag">Has tag</option>
                        </ConfigSelect>
                      )}
                      {node.type === "delay" && (
                        <ConfigInput type="number" placeholder="Minutes" />
                      )}
                      {node.type === "action" && (
                        <ConfigSelect defaultValue="send_template">
                          <option value="send_template">Send template</option>
                          <option value="send_text">Send text</option>
                          <option value="assign">Assign to team</option>
                        </ConfigSelect>
                      )}
                    </StepConfig>
                    <RemoveStep type="button" onClick={() => removeNode(node.id)}>
                      <Trash2 size={14} />
                    </RemoveStep>
                  </StepCard>
                  {index < nodes.length - 1 && <StepConnector />}
                </React.Fragment>
              ))}
            </FlowSteps>

            <AddStepBox>
              <AddStepLabel>Add step</AddStepLabel>
              <AddStepButtons>
                {(Object.keys(NODE_LABELS) as NodeType[]).map((type) => (
                  <AddStepBtn key={type} type="button" onClick={() => addNode(type)}>
                    <Plus size={14} />
                    {NODE_LABELS[type]}
                  </AddStepBtn>
                ))}
              </AddStepButtons>
            </AddStepBox>
          </FlowBody>
        </FlowPanel>

        {previewOpen && (
          <PreviewPanel>
            <PanelHeader>
              <PanelTitleRow>
                <Eye size={16} color="#c94daa" />
                <PanelTitle>Preview</PanelTitle>
              </PanelTitleRow>
            </PanelHeader>
            <PreviewBody>
              {nodes.map((n, i) => (
                <PreviewLine key={n.id}>
                  <PreviewIndex>{i + 1}</PreviewIndex>
                  <PreviewIcon $tone={NODE_TONES[n.type]}>
                    {NODE_ICONS[n.type]}
                  </PreviewIcon>
                  <span>{NODE_LABELS[n.type]}</span>
                </PreviewLine>
              ))}
            </PreviewBody>
          </PreviewPanel>
        )}
      </ContentRow>

      <SaveRow>
        <SaveButton type="button">
          <Save size={16} />
          Save workflow
        </SaveButton>
      </SaveRow>
    </Page>
  );
};

export default AutomationBuilderPage;

const GRADIENT =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Page = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  overflow: hidden;
  font-family: Urbanist, sans-serif;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    transparent;

  @media ${device.tablet} {
    padding: 16px;
    gap: 14px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const BackBtn = styled(Link)`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(23, 23, 23, 0.7);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
    transform: translateX(-1px);
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 480px;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const NameInput = styled.input`
  height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.9);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  min-width: 180px;
  outline: none;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.4);
    font-weight: 500;
  }
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.9);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.7);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatChip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}14`};
  flex-shrink: 0;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
  margin-top: 2px;
`;

const ContentRow = styled.div<{ $preview?: boolean }>`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: ${(p) => (p.$preview ? "1fr 280px" : "1fr")};
  gap: 16px;
  overflow: hidden;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`;

const FlowPanel = styled.div`
  border-radius: 20px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const PreviewPanel = styled.div`
  border-radius: 20px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media ${device.tablet} {
    max-height: 280px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const PanelHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`;

const FlowBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 20px 20px;
`;

const FlowSteps = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  transition: all 0.15s ease;
  &:hover {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.14);
  }
`;

const StepBadge = styled.div<{ $tone: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}14`};
  flex-shrink: 0;
`;

const StepConfig = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConfigSelect = styled.select`
  width: 100%;
  max-width: 240px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  outline: none;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const ConfigInput = styled.input`
  width: 100%;
  max-width: 120px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  outline: none;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const RemoveStep = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  color: rgba(23, 23, 23, 0.35);
  cursor: pointer;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #e44862;
    background: rgba(228, 72, 98, 0.08);
  }
`;

const StepConnector = styled.div`
  width: 2px;
  height: 20px;
  margin-left: 36px;
  background: linear-gradient(180deg, #c94daa 0%, rgba(201, 77, 170, 0.15) 100%);
`;

const AddStepBox = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.08) 0%,
    rgba(206, 86, 176, 0.06) 100%
  );
  border-radius: 16px;
  border: 1px dashed rgba(201, 77, 170, 0.25);
`;

const AddStepLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
`;

const AddStepButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AddStepBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.7);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.3);
    color: #c94daa;
    background: rgba(201, 77, 170, 0.04);
  }
`;

const PreviewBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PreviewLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.05);
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

const PreviewIndex = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
`;

const PreviewIcon = styled.span<{ $tone: string }>`
  display: flex;
  color: ${(p) => p.$tone};
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 12px;
  border: none;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: ${GRADIENT};
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease, opacity 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }
`;
